import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, HostListener } from '@angular/core';
import { Observable, concat, merge, from, of, EMPTY, Subscription, Subject } from 'rxjs';
import { map, switchMap, tap, concatMap, finalize, debounceTime, distinctUntilChanged, filter, catchError } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems } from 'src/app/shared/document';
import { Product, ProductInfo } from 'src/app/shared/product';
import { Currency } from 'src/app/shared/currency';
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * Inline-editing table for specification documents with serial-number scanning workflow.
 *
 * **Pipeline architecture:**
 * - `docItemList$` — debounced `switchMap(getDocItemsByDoc)` that reloads rows when the
 *   document ID changes; runs synchronous `calculateDocumentSummary` after load (no `calcDocSum$`).
 * - `searchProductBySerial$` — debounced `switchMap(getProductBySerialStr)` that finds the
 *   matching row and auto-assigns the scanned serial number.
 * - `saveDiSerial$` — sequential Subject that persists serial-number writes via `putDocItemById`.
 *
 * **Warranty management:** `warrantyOptionsList` provides a fixed list of warranty terms;
 * `updateWarrantyTerms` bulk-applies `warranty` to all selected rows' `DOCITEMTEXT1`.
 *
 * **Duplicate control:** `enableDuplicate` flag lets users allow duplicate serial numbers
 * across rows; when false, duplicates are blocked before assignment.
 *
 * **Row selection helper:** `onSelectedItemChange` auto-selects the first empty row for a
 * product when the user picks a product to speed up serial entry.
 *
 * Column visibility is persisted in `localStorage` under key `edit-document-table-columns-7`.
 */
@Component({
  selector: 'app-edit-document-content-spec',
  templateUrl: './edit-document-content-spec.component.html',
  styleUrls: ['./edit-document-content-spec.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentSpecComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData;
  private docItemQuery: Subscription;
  private diSumQuery: Subscription;
  private diSaveQuery: Subscription;
  private diGroupQuery: Subscription;

  private searchStrQuery: Subscription;

  private _productInfoQuery: Subscription;

  /** Whether to render the table header toolbar. */
  @Input() showHeader: boolean = true;
  /** Whether to show add/edit/delete action controls. */
  @Input() allowEditControls: boolean = true;
  /** Whether to show payment-sum footer row. */
  @Input() showPaysum: boolean = true;

  /** Column header label for the `DOCITEMTEXT2` field (defaults to warranty term). */
  @Input() lblDOCITEMTEXT2: string = 'Гарантия';

  loading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number;

  sDI: DocItems[] = [];

  allDocItemsChanged: boolean = false;

  docItems: DocItems[];

  docItemsCopy: DocItems[];
  docItemsClone: DocItems[] = [];

  @ViewChild('docItemTable') docItemTable: Table;

  //@HostListener('keydown.space', ['$event'])
  //onSpaceKeyDown(event: KeyboardEvent) {
  //  event.stopPropagation();
  //  event.preventDefault();
  //  if (event.target) {
  //    event.target['value'] = event.target['value'] + ' ';
  //    (event.target as HTMLElement).focus();
  //  }
  //}

  editMode: boolean;
  confirmDeleteOn: boolean;

  private docItemList$ = new Subject<number>();
  private searchProductBySerial$ = new Subject<string>();

  private saveDiSerial$ = new Subject<DocItems>();

  /** The document whose line items are being edited. */
  @Input() inputDocument: Document;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-7';

  warranty: string = '24 місяці';

  warrantyOptionsList = [
    { label: '2 тижні',     value: '2 тижні'},
    { label: '1 місяць',    value: '1 місяць' },
    { label: '3 місяці',    value: '3 місяці' },
    { label: '6 місяців',   value: '6 місяців' },
    { label: '12 місяців',  value: '12 місяців' },
    { label: '18 місяців',  value: '18 місяців' },
    { label: '24 місяці',   value: '24 місяці' },
    { label: '36 місяців',  value: '36 місяців' },
    { label: 'Довічна',     value: 'Довічна' },
    { label: 'Нема',        value: 'Нема' },
    { label: '',            value: '' }
  ];

  showDocRowsOptions = [
  //  { name: 'Инвойс', value: 1 },
  //  { name: 'УКТЗЕД', value: 4 },
  //  { name: 'Резерв', value: 8 },
  ];

  showFilterOptions = [
    { name: 'Фильтр', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  allowOld: boolean = false;

  /** Overrides column-visibility bitmask from the caller; skips localStorage restore when set. */
  @Input() setShowRowOptions: number[];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  public get tooMuchItems(): boolean { return ((this.docItems || []).length > 50); }

  public get canSearchAndInsert(): boolean { return !this.noItems && !this.editMode && ((this.serialStr || "").length > 0); }
  public get canSearch(): boolean { return !this.noItems && !this.editMode; }

  /*
   *  TODO: load access right to check is U4ETKA is visible to
   */
  public get seeTariffCode(): boolean { return this.showRowOptions.includes(4); }

  public get seeDiNote(): boolean { return ([1, 12].includes(this.inputDocument.DOCTYPE)); }

  public get selection(): boolean { return (this.sDI || []).length > 0; }

  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public get allowTopButtons(): boolean { return this.docItems && (this.docItems.length > 10); }

  public allowSave(di: DocItems): boolean { return di && (di.PRODID ? true : false); }

  public notIn(str: string): boolean { return !(this.docItems && this.docItems.find(e => ((e.DOCITEMTEXT1 || '') == str))); }

  cf3: any = {};

  serialStr: string = null;
  serialStrings: string[] = [];
  product: { prodid: number; prodname: string; };
  products: { prodid: number; prodname: string; }[] = [];

  serialProdId: number;

  enableDuplicate: boolean = false;

  /** Emits `true` when entering edit mode, `false` when edit is saved or cancelled. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) { }

  reloadDocContent(event) {
    console.log('*** EditDocumentContentSpecComponent reloadDocContent: ' + JSON.stringify(this.inputDocument))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  saveDiCopy() {
    this.docItemsCopy = [];
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));
  }

  restoreDiCopy() {
    this.docItems = [];
    this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));
  }

  calculateRowSummary(di: DocItems) {
    //console.log('*** EditDocumentContentSpecComponent calculateRowSummary: ' + di.DOCITEMID);

    di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
    di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);

    di.mtc = ((di.PRODCUSTCODE || '') == '');

    di.hlt = (di.cnm || di.rnm || di.mtc);

    this.calculateDocumentSummary();
  }

  calculateDocItemsFields(data: DocItems[]) {
    //console.log('*** EditDocumentContentSpecComponent calculateDocItemsFields')
    this.docItems = [...data];

    this.diSumQuery =
      from(this.docItems)
        .pipe(switchMap(di => of(di)))
        .subscribe(di => {

          di.cloneIndex = 0;
        });

    this.inputDocument.items = this.docItems;
    this.saveDiCopy();
  }

  calculateDocumentSummary() {
    //console.log('*** EditDocumentContentSpecComponent calculateDocumentSummary: ')

    if (this.inputDocument) {

      this.inputDocument.DOCITEMS = 0;
      this.inputDocument.DOCTOTALITEMS = 0;

      if (this.docItems) {
        this.inputDocument.DOCITEMS = this.docItems.length;
        this.docItems.forEach((di: DocItems) => {
          this.inputDocument.DOCTOTALITEMS += di.DOCITEMQTY;
        });
      }
    }
  }

  private handleHttpError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      const detail = error?.status === 0 ? `Немає з'єднання з сервером`
        : error?.status >= 500 ? 'Помилка сервера'
        : error?.message || 'Невідома помилка';
      this.messageService.add({ severity: 'error', summary: 'Помилка завантаження', detail, life: 5000 });
      return result !== undefined ? of(result) : (EMPTY as Observable<T>);
    };
  }

  /** Persists column visibility to localStorage and unsubscribes all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentContentSpecComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }

    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }

    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }

    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }

    if (this.searchStrQuery) { this.searchStrQuery.unsubscribe(); }
  }

  /**
   * Reacts to `inputDocument` and `setShowRowOptions` changes.
   * - `inputDocument`: pushes the document ID onto `docItemList$` to reload rows.
   * - `setShowRowOptions`: overrides the column visibility bitmask immediately.
   */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentSpecComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentSpecComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentSpecComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        case 'inputDocument':
          if (change.currentValue) {
            this.docItemList$.next(change.currentValue.DOCID);
          }
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, restores column state from localStorage, and wires up `docItemList$`, `searchProductBySerial$`, and `saveDiSerial$` pipelines. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentSpecComponent ngOnInit: ')


    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    ));

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    ));

    //from([1, 2, 3, 4, 5, 6, 7, 100, 200])
    //  .pipe(
    //    map(data => { console.log(data); return data; }),
        
    //    filter(e => (e < 100)),
    //    map(data => { console.log(data); return data; }),
        
    //)
    //  .subscribe();

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }

    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.diSaveQuery =
      this.saveDiSerial$
      .pipe(
        concatMap(di => this.docApi.putDocItemById(di).pipe(map(c => {
          this.serialStrings.unshift(this.serialStr);
          //          this.serialStrings.push(this.serialStr);

          this.serialStr = '';

          let diNext = this.docItems.findIndex(e => (e.DOCITEMID == di.DOCITEMID));

          if (diNext > -1) {
            while (++diNext < this.docItems.length) {
              if ((this.docItems[diNext].DOCITEMTEXT1 || '').trim() == '') {
                this.sDI = [this.docItems[diNext]];
                break;
              }
            }
          }
          this.calculateRowSummary(di);
        })))
      )
        .subscribe();
/*
 * TODO: Review the sound confirmation for search and save of serial
 *
 * How to play a sound (beep)
 * https://stackoverflow.com/questions/29567580/play-a-beep-sound-on-button-click
 * 
 */
    this.searchStrQuery =
      this.searchProductBySerial$.pipe(
        debounceTime(50),

        distinctUntilChanged((prev, curr) => (!this.enableDuplicate && (prev === curr))),

        filter(s => ((s || '').length > 3)),

        tap(ss => console.log("searchProductBySerial$ going to process: '" + ss + "'")),

        switchMap(sstr => this.prodApi.getProductBySerialStr(sstr, true, true).pipe(


          map(pr => {
            console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (2): ' + JSON.stringify(pr))
          if (pr && pr[0]) {
            this.serialProdId = pr[0].PRODID;

            let existing = this.docItems.find(e => ((e.DOCITEMTEXT1 || '').trim() == this.serialStr));

            if (existing) {
              this.messageService.add({ severity: 'warn', summary: this.serialStr + ': Этот серийный номер уже есть в документе ' });
            }

            if (!existing || this.enableDuplicate) {
              let di = this.docItems.find(e => ((e.PRODID == this.serialProdId) && ((e.DOCITEMTEXT1 || '').trim() == '')));

              console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (3): ' + JSON.stringify(di))

              if (di) {
                console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (4): ' + JSON.stringify(di))
                di.DOCITEMTEXT1 = this.serialStr;
                this.saveDiSerial$.next(di);

              } else {
                this.messageService.add({ severity: 'warn', summary: this.serialStr + ': Не найден такой товар или свободное место ' });
              }
            } else {

              this.sDI = [existing];

            }
          } else {

            if (this.sDI.length > 0) {

              let di = this.sDI[this.sDI.length - 1];

              console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (6): ' + JSON.stringify(di))

              if (di) {
                console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (7): ' + JSON.stringify(di))

                if ((di.DOCITEMTEXT1 || '').trim() == '') {
                  di.DOCITEMTEXT1 = this.serialStr;

                  of(di)
                    .pipe(concatMap(e => this.docApi.putDocItemById(e)))
                    .subscribe(r => {
                      console.log('*** EditDocumentContentSpecComponent searchProductBySerial$ subscription (8): ' + JSON.stringify(r))
                      this.serialStrings.push(sstr);
                      this.serialStr = '';

                      this.calculateRowSummary(di);

                      let next = this.docItems.findIndex(e => e.DOCITEMID == di.DOCITEMID);
                      if (next > -1) {
                        while (++next < this.docItems.length) {
                          if ((this.docItems[next].DOCITEMTEXT1 || '').trim() == '') {
                            this.sDI = [this.docItems[next]];
                            break;
                          }
                        }
                      }
                    });
                } else {
                  this.messageService.add({ severity: 'warn', summary: this.serialStr + ': Выбранная строка для вставки серийного номера занята. Сохранено.' });

                  this.serialStrings.push(this.serialStr);
                  this.serialStr = '';
                }
              }
            } else {

              this.messageService.add({ severity: 'warn', summary: this.serialStr + ': Не выбрана строка для вставки серийного номера. Сохранено.' });

              this.serialStrings.push(sstr);
              this.serialStr = '';
            }
          }
        }))))
        .subscribe();

    this.docItemQuery =
      this.docItemList$.pipe(
        debounceTime(100),
        switchMap((docId: number) => {
          this.loading = true;
          console.log('*** EditDocumentContentSpecComponent docItemList$ subscribing....: ' + docId)

          return this.docApi.getDocItemsByDoc(docId);

        })
      )
        .subscribe(data => {

          console.log('*** EditDocumentContentSpecComponent docItemList$ subscription: ')
          //environment.production || console.table(data);

          // Build set of distinct prodcodes in document
          // https://codeburst.io/javascript-array-distinct-5edc93501dc4
          //
          this.products = Array.from(new Set(data.map(v => v.PRODID))).map(id => ({ prodid: id, prodname: data.find(v => id == v.PRODID).PRODCODE }));

          this.calculateDocItemsFields(data);     // <= Raw docItems[] from database
          this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
          // <= Here we have also document summary calculated
          this.loading = false;
        });

    this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
    //  console.log('*** EditDocumentContentSpecComponent in doChangeConditions: ' + JSON.stringify(event));
    //  console.log('*** EditDocumentContentSpecComponent in doChangeConditions (showRowOptions): ' + JSON.stringify(this.showRowOptions));
  }

  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentSpecComponent onProductSelection: ' + JSON.stringify(event));
    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;

    this._productInfoQuery =
      this.prodApi.getProdInfoById(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCCURRID, this.inputDocument.DOCMANDEPID, 0, 0, new Date())
        .pipe(map(data => {
          di.prodInfo = data[0];
          di.DOCITEMPRODNAME = event.PRODNAME;
        }))
        .subscribe();
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentSpecComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: '',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    };

    this.docItems = [...this.docItems, newDi];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentSpecComponent in onPpRowEditInit: entry');

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentSpecComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.editing.emit(this.editMode);
}

  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentSpecComponent in onPpRowEditCancel: ' + index);
    //environment.production || console.table(esg);

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.DOCITEMID == -2) {
      // Cancel the newly created record
      // simply delete

      this.docItems.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.docItems[index] = { ...this.docItemsClone[ppid] };
      this.docItems[index].cloneIndex = 0;
    }

    this.docItemsClone.splice(ppid, 1);

    this.editMode = false;
    this.editing.emit(this.editMode);
  }

  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentSpecComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.DOCITEMID == -2) {
      // Here we post (save) newly created record
      // and need to reload DocItem content or recevie new DocItemId in response to update the table

      this.diSaveQuery = this.docApi.postDocItemById(esg)
        .pipe(
          map(result => {
            environment.production || console.log('*** EditDocumentContentSpecComponent in onSubscribe to Save (post): ' + + JSON.stringify(result));

            this.docItemsClone.splice(ppid, 1);
            esg.cloneIndex = 0;
            this.reloadDocContent(null);
            this.editMode = false;
            this.editing.emit(this.editMode);
          })
        ).subscribe();

    } else {
      this.diSaveQuery = this.docApi.putDocItemById(esg)
        .pipe(
          map(result => {
            environment.production || console.log('*** EditDocumentContentSpecComponent in onSubscribe to Save (put): ' + + JSON.stringify(result));

            this.docItemsClone.splice(ppid, 1);
            esg.cloneIndex = 0;
            this.reloadDocContent(null);
            this.editMode = false;
            this.editing.emit(this.editMode);
          })
        ).subscribe();
    }

  //  this.diSaveQuery =
  //    concat(((esg.DOCITEMID == -2) ? this.docApi.postDocItemById(esg) : this.docApi.putDocItemById(esg))
  //      .pipe(map((count) => {
  //        console.log('*** EditDocumentContentSpecComponent in onSubscribe to Save: ' + count);

  //        this.docItemsClone.splice(ppid, 1);
  //        esg.cloneIndex = 0;
  //        this.reloadDocContent(null);
  //        this.editMode = false;
  //        this.editing.emit(this.editMode);
  //      })),

  //    )
  //      .subscribe();
  }

  onDOCITEMQTYChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentSpecComponent in onDOCITEMQTYChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentSpecComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    this.calculateRowSummary(di);
  }

  onDateSelect(event, di: DocItems) {
    console.log('*** EditDocumentContentSpecComponent in onDateSelect: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentSpecComponent in onDateSelect: ' + JSON.stringify(di));
    di.DOCITEMTEXT1 = di.DOCITEMPOSHIPPINGDATE.toISOString();
    this.calculateRowSummary(di);
  }

  checkDiState(event, di: DocItems) {
    console.log('*** EditDocumentContentSpecComponent in checkDiState: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentSpecComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {
      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
      //    + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
      //    + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
      //    + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
      //    + (di.wns ? 'Не указан вес товара! ' : '')
      //    + (di.pdm ? 'Не совпадает цена в инвойсе и в базе! ' : '')
      });

    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }
  }

  onRowSelect(event) {
    console.log('*** EditDocumentContentSpecComponent in onRowSelect: ' + JSON.stringify(event));
  }

  onSelectAll(event) {
    console.log('*** EditDocumentContentSpecComponent in onRemoveSelection: ' + JSON.stringify(event));

    this.sDI = this.docItems;
  }

  onRemoveSelection(event) {
    console.log('*** EditDocumentContentSpecComponent in onRemoveSelection: ' + JSON.stringify(event));
    this.sDI = [];
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentSpecComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentSpecComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentSpecComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentSpecComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentSpecComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentSpecComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = [];
            this.reloadDocContent(null);
          });
      } else {

        concat(
          from(this.sDI)
            .pipe(
              concatMap(row => this.docApi.deleteDocItemById(row.DOCITEMID)),
              finalize(() => {
                this.sDI = [];
                this.reloadDocContent(null);
              })
            ))
            .subscribe();
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentSpecComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems.map(e => ({

          'Код товара': e.PRODCODE,
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Таможенный код': e.PRODCUSTCODE,
          'Серийный №': e.DOCITEMTEXT1,
          'Гарантия': e.DOCITEMTEXT2,
        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentSpecComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentSpecComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(state) {
    console.log('*** EditDocumentContentComponent cleanSortMode: ' + JSON.stringify(state));

    this.docItemTable.reset();
    this.reloadDocContent(state);
  }

  /** Bulk-writes `warranty` to `DOCITEMTEXT2` on all selected rows (or all rows if none selected) via `putDocItemById`. */
  updateWarrantyTerms(state) {
    console.log('*** EditDocumentContentComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;
    this.loading = true;

    this.diSaveQuery =
      concat(
        from((this.sDI.length > 0) ? this.sDI : this.docItems)
          .pipe(concatMap(di => {
            di.DOCITEMTEXT2 = this.warranty;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          }),
            finalize(() => (this.loading = false))
          ))
        .subscribe();
  }

  /** Clears `DOCITEMTEXT1` (serial field) on all selected rows (or all rows if none selected) via `putDocItemById`. */
  clearSerialFields(state) {
    console.log('*** EditDocumentContentComponent clearSerialFields: ' + JSON.stringify(state));

    this.loading = true;

    this.diSaveQuery =
      concat(
        from((this.sDI.length > 0) ? this.sDI : this.docItems)
          .pipe(concatMap(di => {
            di.DOCITEMTEXT1 = '';

            return this.docApi.putDocItemById(di);
          }),
          finalize(() => (this.loading = false))
        ))
        .subscribe();
  }

  updateRowIndexes(state) {
    console.log('*** EditDocumentContentComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;
    this.loading = true;

    this.diSaveQuery =
      concat(
        from(this.docItems)
          .pipe(concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          }),
            finalize(() => (this.loading = false))
          ))
        .subscribe();
  }

  onEnterSerial(event) {
    console.log('*** EditDocumentContentSpecComponent in onEnterSerial: ' + JSON.stringify(event));

  }

  onClearSerial(event) {
    console.log('*** EditDocumentContentSpecComponent in onClearSerial: ' + JSON.stringify(event));

    this.serialStr = '';
  }

  onKeyPress(event) {
    console.log('*** EditDocumentContentSpecComponent in onKeyPress: ' + JSON.stringify(event));

    this.searchProductBySerial$.next(this.serialStr);
  }

  onDOCITEMTEXT1EnterKeyPress(event, di: DocItems) {

    console.log('*** EditDocumentContentSpecComponent in onDOCITEMTEXT1EnterKeyPress: ' + JSON.stringify(event));
  }

  /** Auto-selects the first empty-serial row for the chosen product to speed up serial entry. */
  onSelectedItemChange(event) {
    console.log('*** EditDocumentContentSpecComponent in onSelectedItemChange: ' + JSON.stringify(event));

    let di = this.docItems.find(e => ((e.PRODID == event.value) && ((e.DOCITEMTEXT1 || '') == '')));

    console.log('*** EditDocumentContentSpecComponent in onSelectedItemChange: ' + JSON.stringify(di));

    if (di) {
      this.sDI = [di];
    }
  }
  
  onSelectAllItems(event) {
    console.log('*** EditDocumentContentSpecComponent in onSelectAllItems: ' + JSON.stringify(event));

    this.sDI = [...this.sDI, ...this.docItems.filter(e => e.PRODID == this.serialProdId)];
  }

  /** Manually assigns `serialStr` to the first empty-serial row for `serialProdId` and persists via `saveDiSerial$`. */
  searchAndInsert(event) {
    console.log('*** EditDocumentContentSpecComponent in searchAndInsert: ' + JSON.stringify(event));

    let di = this.docItems.find(e => ((e.PRODID == this.serialProdId) && ((e.DOCITEMTEXT1 || '') == '')));

    if (di) {

      this.sDI = [...this.sDI, di];

      di.DOCITEMTEXT1 = this.serialStr;
      this.saveDiSerial$.next(di);
    }
  }
}
